import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { GUID, PersonOrg } from '@ndvr/auth-api';
import { Keyword, KeywordRequest } from '@ndvr/customer-api';

import { CustomerApi } from 'API';

import { useAuthContext } from '../../auth/Auth';

export const useKeywords = () => {
    const { currentTenant } = useAuthContext();
    return useQuery({
        enabled: !!currentTenant?.tenantGUID,
        queryKey: ['tenant', currentTenant?.tenantGUID, 'keywords'],
        queryFn: async () => {
            if (!currentTenant?.tenantGUID) {
                return [];
            }
            const [response] = await CustomerApi().listKeywords(currentTenant.tenantGUID);
            return response ?? [];
        },
    });
};

export const useKeywordsForPersonOrg = (personOrgGUID?: GUID<PersonOrg>) => {
    return useQuery({
        enabled: !!personOrgGUID,
        queryKey: ['personOrg', personOrgGUID, 'keywords'],
        queryFn: async () => {
            if (!personOrgGUID) {
                return [];
            }
            const [response] = await CustomerApi().getKeywordsForPersonOrg(personOrgGUID);
            return response ?? [];
        },
    });
};

export const usePersonOrgsForKeyword = (keywordGUID?: GUID<Keyword>) => {
    return useQuery({
        enabled: !!keywordGUID,
        queryKey: ['keyword', keywordGUID, 'personOrgs'],
        queryFn: async () => {
            if (!keywordGUID) {
                return [];
            }
            const [response] = await CustomerApi().getPersonOrgsByKeyword(keywordGUID);
            return response ?? [];
        },
    });
};

export const useAllKeywordAssociations = (keywords: Keyword[]) => {
    const queries = useQueries({
        queries: keywords.map((keyword) => ({
            queryKey: ['keyword', keyword.keywordGUID, 'personOrgs', 'bulk'],
            queryFn: async () => {
                const [response] = await CustomerApi().getPersonOrgsByKeyword(keyword.keywordGUID);
                return { keywordLabel: keyword.label, keywordGUID: keyword.keywordGUID, personOrgs: response ?? [] };
            },
        })),
    });

    const isLoading = queries.some((q) => q.isLoading);
    const data = queries.map((q) => q.data).filter((d): d is NonNullable<typeof d> => d !== undefined);

    return { data, isLoading };
};

export const useCreateKeyword = () => {
    const queryClient = useQueryClient();
    const { currentTenant } = useAuthContext();

    return useMutation<GUID<Keyword>, Error, KeywordRequest, { existingKeywords?: Keyword[] }>({
        onMutate: () => {
            // Get existing keywords before creation for duplicate detection
            const existingKeywords = queryClient.getQueryData<Keyword[]>([
                'tenant',
                currentTenant?.tenantGUID,
                'keywords',
            ]);

            return { existingKeywords };
        },
        mutationFn: async (request: KeywordRequest) => {
            if (!currentTenant?.tenantGUID) {
                throw new Error('No tenant selected');
            }

            const [response, error] = await CustomerApi().createKeyword(currentTenant.tenantGUID, request);
            if (error) {
                throw error;
            }

            if (!response) {
                throw new Error('Failed to create keyword');
            }

            return response;
        },
        onSuccess: (keywordGUID, variables, context) => {
            queryClient.invalidateQueries({ queryKey: ['tenant', currentTenant?.tenantGUID, 'keywords'] });

            // Check if the returned GUID already exists (meaning it's a duplicate)
            const wasDuplicate =
                context?.existingKeywords?.some((keyword) => keyword.keywordGUID === keywordGUID) ?? false;

            if (wasDuplicate) {
                toast.warning('Keyword already exists');
            } else {
                toast.success('Keyword created successfully');
            }
        },
    });
};

export const useUpdateKeyword = () => {
    const queryClient = useQueryClient();
    const { currentTenant } = useAuthContext();

    return useMutation({
        mutationFn: async ({ keywordGUID, request }: { keywordGUID: GUID<Keyword>; request: KeywordRequest }) => {
            const [, error] = await CustomerApi().updateKeyword(keywordGUID, request);
            if (error) {
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant', currentTenant?.tenantGUID, 'keywords'] });
            toast.success('Keyword updated successfully');
        },
    });
};

export const useDeleteKeyword = () => {
    const queryClient = useQueryClient();
    const { currentTenant } = useAuthContext();

    return useMutation({
        mutationFn: async (keywordGUID: GUID<Keyword>) => {
            const [, error] = await CustomerApi().deleteKeyword(keywordGUID);
            if (error) {
                throw error;
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tenant', currentTenant?.tenantGUID, 'keywords'] });
            toast.success('Keyword deleted successfully');
        },
    });
};

export const useAssociateKeyword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            personOrgGUID,
            keywordGUID,
        }: {
            personOrgGUID: GUID<PersonOrg>;
            keywordGUID: GUID<Keyword>;
        }) => {
            const [, error] = await CustomerApi().associateKeywordWithPersonOrg(personOrgGUID, keywordGUID);
            if (error) {
                throw error;
            }
        },
        onSuccess: async (_, variables) => {
            // Refetch queries and wait for them to complete before updating UI
            await queryClient.refetchQueries({ queryKey: ['keyword', variables.keywordGUID, 'personOrgs'] });
            await queryClient.refetchQueries({ queryKey: ['keyword', variables.keywordGUID, 'personOrgs', 'bulk'] });
            await queryClient.refetchQueries({ queryKey: ['personOrg', variables.personOrgGUID, 'keywords'] });
        },
    });
};

export const useDisassociateKeyword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            personOrgGUID,
            keywordGUID,
        }: {
            personOrgGUID: GUID<PersonOrg>;
            keywordGUID: GUID<Keyword>;
        }) => {
            const [, error] = await CustomerApi().disassociateKeywordFromPersonOrg(personOrgGUID, keywordGUID);
            if (error) {
                throw error;
            }
        },
        onSuccess: async (_, variables) => {
            // Refetch queries and wait for them to complete before updating UI
            await queryClient.refetchQueries({ queryKey: ['keyword', variables.keywordGUID, 'personOrgs'] });
            await queryClient.refetchQueries({ queryKey: ['keyword', variables.keywordGUID, 'personOrgs', 'bulk'] });
            await queryClient.refetchQueries({ queryKey: ['personOrg', variables.personOrgGUID, 'keywords'] });
        },
    });
};
